import React, { useState } from 'react';
import { Mic, MicOff, Video, VideoOff, MonitorUp, PhoneOff, Hand, MoreHorizontal, Users } from 'lucide-react';
import ScreenShareModal from './ScreenShareModal';

interface MeetingControlsProps {
    isMuted: boolean;
    isVideoOn: boolean;
    isScreenSharing: boolean;
    isVideoDisabled?: boolean;
    onToggleMute: () => void;
    onToggleVideo: () => void;
    onToggleScreenShare: () => void;
    onEndCall: () => void;
    onToggleSidebar?: () => void;
    showParticipantsToggle?: boolean;
    onToggleParticipantsSidebar?: () => void;
    isParticipantsSidebarOpen?: boolean;
}

const MeetingControls: React.FC<MeetingControlsProps> = ({
    isMuted,
    isVideoOn,
    isScreenSharing,
    isVideoDisabled = false,
    onToggleMute,
    onToggleVideo,
    onToggleScreenShare,
    onEndCall,
    onToggleSidebar,
    showParticipantsToggle = false,
    onToggleParticipantsSidebar,
    isParticipantsSidebarOpen = false,
}) => {
    const [isScreenShareModalOpen, setIsScreenShareModalOpen] = useState(false);

    const handleScreenShareClick = () => {
        if (isScreenSharing) {
            onToggleScreenShare();
        } else {
            setIsScreenShareModalOpen(true);
        }
    };

    return (
        <>
            <div className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl">
                {/* Mute/Unmute */}
                <button
                    onClick={onToggleMute}
                    className={`p-3 sm:p-3.5 rounded-full transition-all duration-200 ${isMuted
                        ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                        }`}
                    title={isMuted ? 'Unmute' : 'Mute'}
                >
                    {isMuted ? (
                        <MicOff className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : (
                        <Mic className="w-5 h-5 sm:w-6 sm:h-6" />
                    )}
                </button>

                {/* Video On/Off */}
                <button
                    onClick={onToggleVideo}
                    disabled={isVideoDisabled}
                    className={`p-3 sm:p-3.5 rounded-full transition-all duration-200 ${isVideoDisabled
                        ? 'bg-white/5 text-white/30 cursor-not-allowed'
                        : !isVideoOn
                            ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30'
                            : 'bg-white/10 hover:bg-white/20 text-white'
                        }`}
                    title={isVideoDisabled ? 'Turn off screen sharing to enable video' : isVideoOn ? 'Turn off video' : 'Turn on video'}
                >
                    {isVideoOn ? (
                        <Video className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : (
                        <VideoOff className="w-5 h-5 sm:w-6 sm:h-6" />
                    )}
                </button>

                {/* Screen Share */}
                <button
                    onClick={handleScreenShareClick}
                    className={`p-3 sm:p-3.5 rounded-full transition-all duration-200 ${isScreenSharing
                        ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                        }`}
                    title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                >
                    <MonitorUp className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>


                {/* End Call */}
                <button
                    onClick={onEndCall}
                    className="p-3 sm:p-3.5 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30 transition-all duration-200"
                    title="End call"
                >
                    <PhoneOff className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>

                {/* Participants toggle - mobile only, shown when video is pinned
                {showParticipantsToggle && onToggleParticipantsSidebar && (
                    <button
                        onClick={onToggleParticipantsSidebar}
                        className={`p-3 sm:p-3.5 rounded-full transition-all duration-200 sm:hidden ${isParticipantsSidebarOpen
                                ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/30'
                                : 'bg-white/10 hover:bg-white/20 text-white'
                            }`}
                        title="Show participants"
                    >
                        <Users className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                )} */}

                {/* More options */}
                <button
                    onClick={onToggleSidebar}
                    className="p-3 sm:p-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 lg:hidden"
                    title="More options"
                >
                    <MoreHorizontal className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
            </div>

            <ScreenShareModal
                isOpen={isScreenShareModalOpen}
                onClose={() => setIsScreenShareModalOpen(false)}
                onStartShare={onToggleScreenShare}
            />
        </>
    );
};

export default MeetingControls;
